import type { ControllerSession } from '../controller';
import {
  MAX_CLIPBOARD_LENGTH,
  type ClipboardAckMessage,
  type ClipboardReadMessage,
  type ClipboardWriteMessage,
  type HostMessage,
} from '../../shared/datachannel';
import { MAX_CLIPBOARD_HTML_LENGTH, sanitizeClipboardHtml } from '../../shared/clipboard-html';
import { MAX_CLIPBOARD_RTF_LENGTH, sanitizeClipboardRtf } from '../../shared/clipboard-rtf';
import {
  MAX_CLIPBOARD_FILE_BYTES,
  MAX_CLIPBOARD_FILE_COUNT,
  type ClipboardFileData,
} from '../../shared/clipboard-file';
import {
  FILE_TRANSFER_MAX_RETRIES,
  FILE_TRANSFER_RETRY_DELAY_MS,
  FileTransferCancelledError,
  isRetryableFileTransferError,
  type FileTransferProgress,
} from '../../shared/filexfer';
import {
  CLIPBOARD_ACK_TIMEOUT_MS,
  CLIPBOARD_MAX_ATTEMPTS,
  FILE_CLIPBOARD_ACK_TIMEOUT_MS,
  type PendingClipboardTransfer,
  type PendingFileClipboardTransfer,
} from './types';

export const controllerClipboard = {
  onClipboardMessage(this: ControllerSession, raw: unknown, replyChannel: RTCDataChannel | null): void {
    if (replyChannel && replyChannel !== this.clipboardDc && replyChannel !== this.dc) return;
    if (this.accessMode === 'view') return;
    const parsed = this.clipboardMessageGuard.parse(raw);
    if (!parsed) return;
    this.markControlActivity();
    try {
      const msg = parsed as HostMessage;
      if (msg.type === 'clipboard-ack') {
        this.finishClipboardTransfer(msg.transferId, msg.ok);
        if (!msg.ok) this.cb.onStatus('被控端剪贴板写入失败，请重试', 'error');
        return;
      }
      if (msg.type !== 'clipboard' || typeof msg.text !== 'string') return;
      if (msg.text.length > MAX_CLIPBOARD_LENGTH) {
        this.cb.onClipboard?.(msg.text.slice(0, MAX_CLIPBOARD_LENGTH), false);
        if (msg.transferId) this.sendClipboardAck(msg.transferId, false, replyChannel);
        return;
      }
      // HTML 是可选增强层；即使远端没有提供合法富文本，也必须保留
      // text/plain 回退，避免格式数据异常导致用户连纯文本都收不到。
      const html = typeof msg.html === 'string' && msg.html.length <= MAX_CLIPBOARD_HTML_LENGTH
        ? sanitizeClipboardHtml(msg.html)
        : '';
      const rtf = typeof msg.rtf === 'string' && msg.rtf.length <= MAX_CLIPBOARD_RTF_LENGTH
        ? sanitizeClipboardRtf(msg.rtf)
        : '';
      const revision = ++this.clipboardReceiveRevision;
      // 不让多个系统剪贴板写入并行执行。Windows 剪贴板经常被其他进程
      // 短暂锁定；串行化后，快速连续复制不会出现旧内容覆盖新内容。
      this.clipboardWriteTail = this.clipboardWriteTail
        .catch(() => {})
        .then(async () => {
          if (this.disposed || revision !== this.clipboardReceiveRevision) {
            // 已被更新的内容不再重复写入，但仍确认消息已被消费，避免
            // 被控端为旧 transferId 无限重试。
            if (!this.disposed && msg.transferId) this.sendClipboardAck(msg.transferId, true, replyChannel);
            return;
          }
          try {
            let copied = false;
            if ((html || rtf) && this.api.writeClipboardHtml) {
              copied = await this.api.writeClipboardHtml(msg.text, html, rtf || undefined);
            }
            if (!copied) copied = await this.api.writeClipboard(msg.text);
            const ok = copied !== false;
            if (revision === this.clipboardReceiveRevision && !this.disposed) {
              this.cb.onClipboard?.(msg.text, ok);
            }
            if (msg.transferId && !this.disposed) this.sendClipboardAck(msg.transferId, ok, replyChannel);
          } catch {
            if (revision === this.clipboardReceiveRevision && !this.disposed) {
              this.cb.onClipboard?.(msg.text, false);
            }
            if (msg.transferId && !this.disposed) this.sendClipboardAck(msg.transferId, false, replyChannel);
          }
        });
    } catch {
      // 剪贴板是增强通道；单条损坏消息不能影响输入和视频。
    }
  },

  sendClipboardAck(
    this: ControllerSession,
    transferId: string,
    ok: boolean,
    preferredChannel: RTCDataChannel | null,
  ): void {
    if (preferredChannel && preferredChannel !== this.clipboardDc && preferredChannel !== this.dc) return;
    const channel = preferredChannel?.readyState === 'open'
      ? preferredChannel
      : this.clipboardDc?.readyState === 'open'
        ? this.clipboardDc
        : this.dc?.readyState === 'open'
          ? this.dc
          : null;
    if (!channel) return;
    try {
      channel.send(JSON.stringify({ type: 'clipboard-ack', transferId, ok } as ClipboardAckMessage));
    } catch {
      // 对端会在没有回执时自动重试；这里不阻塞控制线程。
    }
  },

  requestRemoteClipboard(this: ControllerSession): void {
    if (this.accessMode === 'view') return;
    const channel = this.clipboardChannel();
    if (!channel) {
      this.cb.onStatus('剪贴板通道尚未就绪，请稍候再试', 'error');
      return;
    }
    const message: ClipboardReadMessage = {
      type: 'clipboard-read',
      requestId: this.nextClipboardId('read'),
    };
    try {
      channel.send(JSON.stringify(message));
    } catch {
      this.cb.onStatus('读取远端剪贴板失败，请重试', 'error');
    }
  },

  /** 发送本机剪贴板，并等待被控端确认已经写入系统剪贴板。 */
  async sendClipboardText(this: ControllerSession, text: string, html?: string, rtf?: string): Promise<boolean> {
    if (this.accessMode === 'view') return false;
    if (text.length > MAX_CLIPBOARD_LENGTH) {
      this.cb.onStatus('剪贴板内容过大，已跳过发送', 'error');
      return false;
    }
    if (html !== undefined && html.length > MAX_CLIPBOARD_HTML_LENGTH) html = '';
    const safeHtml = html ? sanitizeClipboardHtml(html) : '';
    if (rtf !== undefined && rtf.length > MAX_CLIPBOARD_RTF_LENGTH) rtf = '';
    const safeRtf = rtf ? sanitizeClipboardRtf(rtf) : '';
    const channel = this.clipboardChannel();
    if (!channel) {
      this.cb.onStatus('剪贴板通道尚未就绪，请稍候再试', 'error');
      return false;
    }

    const id = this.nextClipboardId('write');
    return new Promise<boolean>((resolve) => {
      const pending = {
        id,
        text,
        ...(safeHtml ? { html: safeHtml } : {}),
        ...(safeRtf ? { rtf: safeRtf } : {}),
        channel,
        attempts: 0,
        timer: null,
        resolve,
      } as PendingClipboardTransfer;
      this.pendingClipboardTransfers.set(id, pending);
      this.sendClipboardAttempt(pending);
    });
  },

  async pasteToRemote(this: ControllerSession): Promise<boolean> {
    if (this.accessMode === 'view') return false;
    try {
      const files = await this.api.readClipboardFiles?.();
      if (files && files.length > 0) {
        if (await this.sendClipboardFiles(files)) {
          this.cb.onStatus('文件已发送到被控端剪贴板', 'online');
          return true;
        }
        return false;
      }
      const text = await this.api.readClipboard();
      const html = await this.api.readClipboardHtml?.();
      const rtf = await this.api.readClipboardRtf?.();
      if (!text && !html && !rtf) {
        this.cb.onStatus('本机剪贴板为空，或浏览器未授权读取', 'error');
        return false;
      }
      if (await this.sendClipboardText(text, html, rtf)) {
        this.cb.onStatus('已发送到被控端剪贴板', 'online');
        return true;
      }
      return false;
    } catch {
      this.cb.onStatus('读取本地剪贴板失败', 'error');
      return false;
    }
  },

  async sendClipboardFiles(this: ControllerSession, files: ClipboardFileData[]): Promise<boolean> {
    if (this.accessMode === 'view') return false;
    if (!files.length) return false;
    const totalBytes = files.reduce((total, file) => total + file.data.byteLength, 0);
    if (files.length > MAX_CLIPBOARD_FILE_COUNT || totalBytes > MAX_CLIPBOARD_FILE_BYTES) {
      this.cb.onStatus(`文件剪贴板不能超过 ${MAX_CLIPBOARD_FILE_COUNT} 个或 ${MAX_CLIPBOARD_FILE_BYTES / 1024 / 1024 / 1024}GB`, 'error');
      return false;
    }
    return this.enqueueFileSend(() => this.sendClipboardFilesNow(files));
  },

  async sendClipboardFilesNow(this: ControllerSession, files: ClipboardFileData[]): Promise<boolean> {
    if (this.accessMode === 'view') return false;
    try {
      await this.waitForFileChannel();
    } catch (error) {
      this.cb.onStatus(error instanceof Error ? error.message : '文件通道尚未就绪，请稍候再试', 'error');
      return false;
    }
    const batchId = `clipboard-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const pending = await new Promise<boolean>((resolve) => {
      const transfer: PendingFileClipboardTransfer = { id: batchId, resolve, timer: null };
      this.pendingFileClipboardTransfers.set(batchId, transfer);
      transfer.timer = setTimeout(() => {
        if (this.pendingFileClipboardTransfers.get(batchId) !== transfer) return;
        this.finishFileClipboardTransfer(batchId, false);
        this.cb.onStatus('文件剪贴板传输超时，请重试', 'error');
      }, FILE_CLIPBOARD_ACK_TIMEOUT_MS);
      void (async () => {
        try {
          for (let index = 0; index < files.length; index += 1) {
            const file = files[index];
            const transferId = `clipboard-file-${batchId}-${index}`;
            const emit = (transferred: number, state: FileTransferProgress['state'], error?: string): void => {
              this.cb.onFileTransfer?.({
                id: transferId,
                name: file.name,
                size: file.data.byteLength,
                transferred: Math.min(Math.max(transferred, 0), file.data.byteLength),
                direction: 'send',
                state,
                error,
              });
            };
            emit(0, 'transferring');
            let lastTransferred = 0;
            for (let attempt = 0; ; attempt += 1) {
              try {
                await this.sendFilePayload(files[index], {
                  purpose: 'clipboard',
                  clipboardId: batchId,
                  clipboardIndex: index,
                  clipboardTotal: files.length,
                }, transferId, (transferred) => {
                  lastTransferred = transferred;
                  emit(transferred, 'transferring');
                }, attempt > 0);
                emit(file.data.byteLength, 'completed');
                this.fileAckOffsets.delete(transferId);
                break;
              } catch (error) {
                const cancelled = error instanceof FileTransferCancelledError;
                if (
                  cancelled ||
                  attempt >= FILE_TRANSFER_MAX_RETRIES ||
                  this.disposed ||
                  !isRetryableFileTransferError(error)
                ) {
                  this.fileAckOffsets.delete(transferId);
                  throw error;
                }
                this.cb.onStatus(
                  `连接出现波动，正在恢复剪贴板文件（${attempt + 1}/${FILE_TRANSFER_MAX_RETRIES}）`,
                  'idle',
                );
                emit(lastTransferred, 'transferring');
                this.activeFileTransfers.add(transferId);
                await new Promise<void>((resolve) => setTimeout(resolve, FILE_TRANSFER_RETRY_DELAY_MS * (attempt + 1)));
                this.activeFileTransfers.delete(transferId);
                if (this.cancelledFileTransfers.has(transferId)) {
                  this.cancelledFileTransfers.delete(transferId);
                  throw new FileTransferCancelledError();
                }
                if (this.disposed) throw error;
                // The next attempt asks the receiver for its confirmed offset;
                // keep the last visible progress until that response arrives.
              }
            }
          }
        } catch (err) {
          this.finishFileClipboardTransfer(batchId, false);
          if (err instanceof FileTransferCancelledError) {
            this.cb.onStatus('文件传输已取消', 'idle');
          } else {
            this.cb.onStatus(err instanceof Error ? err.message : '文件发送失败，请重试', 'error');
          }
        }
      })();
    });
    return pending;
  },

  clipboardChannel(this: ControllerSession): RTCDataChannel | null {
    return this.clipboardDc?.readyState === 'open'
      ? this.clipboardDc
      : this.dc?.readyState === 'open'
        ? this.dc
        : null;
  },

  nextClipboardId(this: ControllerSession, kind: 'read' | 'write'): string {
    this.clipboardSequence += 1;
    return `${kind}-${Date.now().toString(36)}-${this.clipboardSequence.toString(36)}`;
  },

  sendClipboardAttempt(this: ControllerSession, pending: PendingClipboardTransfer): void {
    if (this.pendingClipboardTransfers.get(pending.id) !== pending) return;
    const availableChannel = pending.channel.readyState === 'open'
      ? pending.channel
      : this.clipboardChannel();
    if (!availableChannel) {
      this.finishClipboardTransfer(pending.id, false);
      this.cb.onStatus('剪贴板通道已断开，请重试', 'error');
      return;
    }
    pending.channel = availableChannel;
    const message: ClipboardWriteMessage = {
      type: 'clipboard',
      text: pending.text,
      ...(pending.html ? { html: pending.html } : {}),
      ...(pending.rtf ? { rtf: pending.rtf } : {}),
      transferId: pending.id,
    };
    try {
      pending.channel.send(JSON.stringify(message));
    } catch {
      if (pending.attempts < CLIPBOARD_MAX_ATTEMPTS - 1) {
        pending.attempts += 1;
        pending.timer = setTimeout(() => this.sendClipboardAttempt(pending), 120);
        return;
      }
      this.finishClipboardTransfer(pending.id, false);
      this.cb.onStatus('发送到被控端失败，请重试', 'error');
      return;
    }

    pending.timer = setTimeout(() => {
      if (this.pendingClipboardTransfers.get(pending.id) !== pending) return;
      if (pending.attempts < CLIPBOARD_MAX_ATTEMPTS - 1) {
        pending.attempts += 1;
        this.sendClipboardAttempt(pending);
        return;
      }
      this.finishClipboardTransfer(pending.id, false);
      this.cb.onStatus('被控端未确认剪贴板写入，请重试', 'error');
    }, CLIPBOARD_ACK_TIMEOUT_MS);
  },

  finishClipboardTransfer(this: ControllerSession, id: string, ok: boolean): void {
    const pending = this.pendingClipboardTransfers.get(id);
    if (!pending) return;
    this.pendingClipboardTransfers.delete(id);
    if (pending.timer) clearTimeout(pending.timer);
    pending.resolve(ok);
  },

  clearClipboardTransfers(this: ControllerSession): void {
    for (const pending of this.pendingClipboardTransfers.values()) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.resolve(false);
    }
    this.pendingClipboardTransfers.clear();
  },
};
